import { ConfigService } from './config'

// Confirmação de senha para ações sensíveis (step-up).
//
// O backend é quem decide o que é sensível: o endpoint protegido responde 403 com
// { error: 'reauth_required', action, resourceId }. O Request intercepta essa resposta, chama o
// resolve() daqui — que abre o modal de senha e troca a senha por um token de ~90s no users-api — e
// repete a chamada original com o token no header X-Reauth-Token. Nenhuma tela precisa saber quais
// ações exigem senha.

export interface ReauthChallenge {
    action: string
    resourceId: string | null
    reason?: string
}

// O modal recebe a troca senha→token como função em vez de devolver a senha: assim uma senha errada
// mantém a janela aberta com o aviso, em vez de fechar e estourar o erro na tela de trás.
type ReauthPrompt = (
    challenge: ReauthChallenge,
    confirm: (password: string) => Promise<string>,
) => Promise<string | null>

export class ReauthService {
    private prompt: ReauthPrompt = null
    private config = new ConfigService()

    // registrado uma única vez pelo <ReauthPrompt /> montado na raiz do app
    registerPrompt(fn: ReauthPrompt) {
        this.prompt = fn
    }

    isChallenge(result: { statusCode?: number; json?: any }): boolean {
        return result?.statusCode === 403 && result?.json?.error === 'reauth_required'
    }

    challengeOf(result: { json?: any }): ReauthChallenge {
        return {
            action: result?.json?.action,
            resourceId: result?.json?.resourceId ?? null,
            reason: result?.json?.reason,
        }
    }

    // devolve o token de reauth, ou null se o usuário cancelou (ou se não há modal montado, como no
    // uso do módulo fora do browser)
    async resolve(challenge: ReauthChallenge, sessionToken: string): Promise<string> {
        if (!this.prompt || !sessionToken) return null
        return await this.prompt(challenge, (password) => this.exchange(password, challenge, sessionToken))
    }

    // fetch cru de propósito: passar pelo Request faria o interceptador chamar a si mesmo
    private async exchange(password: string, challenge: ReauthChallenge, sessionToken: string): Promise<string> {
        const url = this.config.userApiUrl + '/auth2/reauth?token=' + sessionToken
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                password,
                action: challenge.action,
                resourceId: challenge.resourceId || undefined,
            }),
        })
        let json = null
        try {
            json = await response.json()
        } catch (error) {}
        if (String(response.status).substring(0, 1) !== '2') {
            throw json?.message || 'Não foi possível confirmar a senha'
        }
        return json?.token
    }
}

export const reauth = new ReauthService()
